import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { FaMotorcycle, FaMoneyBillWave, FaChartLine, FaInfoCircle } from 'react-icons/fa';
import Chart from 'react-apexcharts';

const BikeEMICalculator = () => {
  const [formData, setFormData] = useState({
    loanAmount: '',
    interestRate: '',
    loanTenure: '',
    processingFee: '',
    insurance: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculateEMI = () => {
    const { loanAmount, interestRate, loanTenure, processingFee, insurance } = formData;

    if (!loanAmount || !interestRate || !loanTenure) {
      setError('Please fill in all required fields.');
      return;
    }

    const P = parseFloat(loanAmount);
    const R = parseFloat(interestRate) / 12 / 100;
    const N = parseFloat(loanTenure) * 12;
    const PF = parseFloat(processingFee) || 0;
    const INS = parseFloat(insurance) || 0;

    // Calculate EMI using formula
    const EMI = P * R * Math.pow(1 + R, N) / (Math.pow(1 + R, N) - 1);
    const totalInterest = EMI * N - P;
    const totalPayment = EMI * N;
    const totalCost = totalPayment + PF + INS;

    // Calculate yearly payments
    const yearlyData = [];
    let totalPaid = 0;
    let remainingLoan = P;

    for (let year = 1; year <= loanTenure; year++) {
      let yearlyInterest = 0;
      let yearlyPrincipal = 0;

      for (let month = 1; month <= 12; month++) {
        const monthlyInterest = remainingLoan * R;
        const monthlyPrincipal = EMI - monthlyInterest;
        yearlyInterest += monthlyInterest;
        yearlyPrincipal += monthlyPrincipal;
        remainingLoan -= monthlyPrincipal;
      }

      totalPaid += yearlyPrincipal + yearlyInterest;
      yearlyData.push({
        year,
        interest: yearlyInterest,
        principal: yearlyPrincipal,
        total: yearlyPrincipal + yearlyInterest,
        remaining: remainingLoan
      });
    }

    setResults({
      emi: EMI.toFixed(2),
      totalInterest: totalInterest.toFixed(2),
      totalPayment: totalPayment.toFixed(2),
      totalCost: totalCost.toFixed(2),
      yearlyData,
      processingFee: PF.toFixed(2),
      insurance: INS.toFixed(2)
    });
    setError('');
  };

  const getChartOptions = () => {
    if (!results?.yearlyData) return {};

    const years = results.yearlyData.map(data => `Year ${data.year}`);
    const interest = results.yearlyData.map(data => data.interest);
    const principal = results.yearlyData.map(data => data.principal);

    const maxValue = Math.max(...interest, ...principal);

    return {
      chart: {
        type: 'line',
        height: 400,
        toolbar: {
          show: false
        },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800
        },
        zoom: {
          enabled: false
        }
      },
      stroke: {
        width: [3, 3],
        curve: 'smooth'
      },
      series: [
        {
          name: 'Interest',
          data: interest
        },
        {
          name: 'Principal',
          data: principal
        }
      ],
      xaxis: {
        categories: years,
        title: {
          text: 'Year',
          style: {
            color: '#333',
            fontSize: '14px'
          }
        },
        labels: {
          style: {
            colors: '#666',
            fontSize: '12px'
          }
        }
      },
      yaxis: {
        title: {
          text: 'Amount (₹)',
          style: {
            color: '#333',
            fontSize: '14px'
          }
        },
        labels: {
          style: {
            colors: '#666',
            fontSize: '12px'
          },
          formatter: function (val) {
            return '₹' + val.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
          },
          min: 0,
          max: maxValue * 1.1,
          tickAmount: 5
        }
      },
      legend: {
        position: 'top',
        horizontalAlign: 'center',
        fontSize: '14px',
        markers: {
          width: 12,
          height: 12
        }
      },
      colors: ['#EF4444', '#3B82F6'],
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.2,
          stops: [0, 90, 100]
        }
      },
      tooltip: {
        enabled: true,
        y: {
          formatter: function (val) {
            return '₹' + val.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
          }
        },
        marker: {
          size: 8
        }
      },
      grid: {
        show: true,
        borderColor: '#e0e0e0',
        strokeDashArray: 0,
        position: 'back'
      }
    };
  };

  return (
    <div className="calculator-container bg-gradient-to-r from-blue-500 to-cyan-500 p-4 text-white">
      <Helmet>
        <title>Bike EMI Calculator</title>
        <meta name="description" content="Calculate your bike loan EMI and total cost." />
      </Helmet>

      <div className="calculator-header text-center mb-8">
        <FaMotorcycle className="calculator-icon text-4xl text-white" />
        <h1 className="text-3xl font-bold text-red-500 mb-2">Bike EMI Calculator</h1>
        <p className="text-gray-300">Calculate your bike loan EMI and total cost</p>
      </div>

      <div className="calculator-content max-w-3xl mx-auto bg-white bg-opacity-90 p-8 rounded-lg shadow-lg">
        <div className="calculator-form-section">
          <form onSubmit={(e) => e.preventDefault()} className="calculator-form space-y-6">
            <div className="input-group">
              <label htmlFor="loanAmount" className="block text-gray-700 font-bold mb-2">
                <FaMoneyBillWave className="inline-block mr-2 text-gray-700" /> Loan Amount
              </label>
              <input
                type="number"
                id="loanAmount"
                name="loanAmount"
                value={formData.loanAmount}
                onChange={handleChange}
                placeholder="Enter loan amount"
                required
                className="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:ring-opacity-50 bg-gray-50 text-gray-900"
              />
            </div>

            <div className="input-group">
              <label htmlFor="interestRate" className="block text-gray-700 font-bold mb-2">
                <FaChartLine className="inline-block mr-2 text-gray-700" /> Interest Rate (% p.a.)
              </label>
              <input
                type="number"
                id="interestRate"
                name="interestRate"
                value={formData.interestRate}
                onChange={handleChange}
                placeholder="Enter interest rate"
                required
                className="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:ring-opacity-50 bg-gray-50 text-gray-900"
              />
            </div>

            <div className="input-group">
              <label htmlFor="loanTenure" className="block text-gray-700 font-bold mb-2">
                <FaChartLine className="inline-block mr-2 text-gray-700" /> Loan Tenure (Years)
              </label>
              <input
                type="number"
                id="loanTenure"
                name="loanTenure"
                value={formData.loanTenure}
                onChange={handleChange}
                placeholder="Enter loan tenure"
                required
                className="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:ring-opacity-50 bg-gray-50 text-gray-900"
              />
            </div>

            <div className="input-group">
              <label htmlFor="processingFee" className="block text-gray-700 font-bold mb-2">
                <FaMoneyBillWave className="inline-block mr-2 text-gray-700" /> Processing Fee
              </label>
              <input
                type="number"
                id="processingFee"
                name="processingFee"
                value={formData.processingFee}
                onChange={handleChange}
                placeholder="Enter processing fee"
                className="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:ring-opacity-50 bg-gray-50 text-gray-900"
              />
            </div>

            <div className="input-group">
              <label htmlFor="insurance" className="block text-gray-700 font-bold mb-2">
                <FaMoneyBillWave className="inline-block mr-2 text-gray-700" /> Insurance
              </label>
              <input
                type="number"
                id="insurance"
                name="insurance"
                value={formData.insurance}
                onChange={handleChange}
                placeholder="Enter insurance amount"
                className="w-full border border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:ring-opacity-50 bg-gray-50 text-gray-900"
              />
            </div>

            <button
              onClick={calculateEMI}
              className="calculate-button w-full bg-blue-500 text-white rounded-md py-3 px-6 font-semibold hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all duration-200"
            >
              Calculate EMI
            </button>

            {error && (
              <div className="error-message bg-red-50 text-red-600 rounded-md p-3 mt-4">
                {error}
              </div>
            )}
          </form>
        </div>

        {results && (
          <div className="results-section mt-8">
            <div className="results-header flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">EMI Summary</h2>
              <FaChartLine className="results-icon text-3xl text-gray-900" />
            </div>

            <div className="results-grid grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="result-item bg-white bg-opacity-90 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Monthly EMI</h3>
                    <p className="text-gray-600">Monthly installment amount</p>
                  </div>
                  <div className="text-3xl font-bold text-blue-500">
                    ₹{results.emi}
                  </div>
                </div>
              </div>

              <div className="result-item bg-white bg-opacity-90 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Total Interest</h3>
                    <p className="text-gray-600">Total interest paid</p>
                  </div>
                  <div className="text-3xl font-bold text-blue-500">
                    ₹{results.totalInterest}
                  </div>
                </div>
              </div>

              <div className="result-item bg-white bg-opacity-90 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Total Payment</h3>
                    <p className="text-gray-600">Total loan repayment</p>
                  </div>
                  <div className="text-3xl font-bold text-blue-500">
                    ₹{results.totalPayment}
                  </div>
                </div>
              </div>

              <div className="result-item bg-white bg-opacity-90 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Total Cost</h3>
                    <p className="text-gray-600">Total cost including fees</p>
                  </div>
                  <div className="text-3xl font-bold text-blue-500">
                    ₹{results.totalCost}
                  </div>
                </div>
              </div>
            </div>

            {results?.yearlyData && (
              <div className="chart-section mt-8 bg-white text-black bg-opacity-90 rounded-lg p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">EMI Breakdown Over Time</h2>
                <Chart
                  options={getChartOptions()}
                  series={getChartOptions().series}
                  type="line"
                  height={400}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <div className="calculator-features mt-8 max-w-3xl mx-auto bg-white bg-opacity-90 p-8 rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Key Features & Benefits</h2>
        <div className="features-grid grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="feature-item p-4 rounded-lg bg-white bg-opacity-90 hover:bg-opacity-100 transition-all duration-200">
            <FaMotorcycle className="feature-icon text-3xl text-yellow-500 mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">Bike Loan Specific</h3>
            <p className="text-gray-700">Calculate EMI for your bike loan.</p>
          </div>
          <div className="feature-item p-4 rounded-lg bg-white bg-opacity-90 hover:bg-opacity-100 transition-all duration-200">
            <FaMoneyBillWave className="feature-icon text-3xl text-green-500 mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">Additional Costs</h3>
            <p className="text-gray-700">Include processing fee and insurance.</p>
          </div>
          <div className="feature-item p-4 rounded-lg bg-white bg-opacity-90 hover:bg-opacity-100 transition-all duration-200">
            <FaChartLine className="feature-icon text-3xl text-blue-500 mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">Detailed Breakdown</h3>
            <p className="text-gray-700">See interest vs principal payments.</p>
          </div>
        </div>
      </div>

      <div className="how-to-use mt-8 max-w-3xl mx-auto bg-white bg-opacity-90 p-8 rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">How to Use</h2>
        <ol className="usage-steps list-decimal pl-5 space-y-3">
          <li className="text-gray-700">Enter your bike loan amount</li>
          <li className="text-gray-700">Enter the interest rate</li>
          <li className="text-gray-700">Enter the loan tenure in years</li>
          <li className="text-gray-700">Enter processing fee (optional)</li>
          <li className="text-gray-700">Enter insurance amount (optional)</li>
          <li className="text-gray-700">Click "Calculate EMI" to see your results</li>
        </ol>
      </div>

      <div className="faq-section mt-8 max-w-3xl mx-auto bg-white bg-opacity-90 p-8 rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
        <div className="faq-grid grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="faq-item p-4 rounded-lg bg-white bg-opacity-90 hover:bg-opacity-100 transition-all duration-200">
            <FaInfoCircle className="faq-icon text-3xl text-gray-700 mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">What is EMI?</h3>
            <p className="text-gray-700">EMI stands for Equated Monthly Installment - the fixed amount you pay monthly to repay your loan.</p>
          </div>
          <div className="faq-item p-4 rounded-lg bg-white bg-opacity-90 hover:bg-opacity-100 transition-all duration-200">
            <FaInfoCircle className="faq-icon text-3xl text-gray-700 mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">How is EMI calculated?</h3>
            <p className="text-gray-700">EMI is calculated using the formula: P × R × (1+R)^N/[(1+R)^N-1]</p>
          </div>
          <div className="faq-item p-4 rounded-lg bg-white bg-opacity-90 hover:bg-opacity-100 transition-all duration-200">
            <FaInfoCircle className="faq-icon text-3xl text-gray-700 mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">What is processing fee?</h3>
            <p className="text-gray-700">Processing fee is charged by the bank for processing your loan application.</p>
          </div>
          <div className="faq-item p-4 rounded-lg bg-white bg-opacity-90 hover:bg-opacity-100 transition-all duration-200">
            <FaInfoCircle className="faq-icon text-3xl text-gray-700 mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">What is included in total cost?</h3>
            <p className="text-gray-700">Total cost includes principal amount, interest, processing fee, and insurance.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BikeEMICalculator;
